import { adSlug } from "@/lib/seo";
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, Crown, Eye, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminCard, AdminPageHeader } from "@/components/admin/admin-shell";
import { formatPrice } from "@/lib/data";
import { azError } from "@/lib/az-errors";
import {
  deleteListing,
  fetchAllListings,
  setListingStatus,
  setListingBadge,
  setListingFlag,
  hasListingFlag,
  type ListingFlag,
  type AdminListing,
} from "@/lib/admin";

import { PLACEHOLDER_IMAGE, statusLabels } from "@/lib/listings";

export const Route = createFileRoute("/_authenticated/admin/elanlar")({
  component: Page,
});

function statusVariant(status: string) {
  if (status === "active") return "secondary" as const;
  if (status === "rejected") return "destructive" as const;
  return "outline" as const;
}

type StatusItem = { key: string; label: string; value: number };

function StatusAccordion({ items, onSelect, selected }: { items: StatusItem[]; onSelect: (key: string) => void; selected?: string | null }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold transition-colors hover:bg-accent/50"
        aria-expanded={open}
      >
        <ChevronDown className={`size-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        <span className="flex-1">Elan statusları</span>
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {items.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onSelect(c.key)}
                className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-xs transition-colors ${
                  selected === c.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border/60 bg-background hover:bg-accent"
                }`}
              >
                <span className="truncate">{c.label}</span>
                <span className="shrink-0 font-bold tabular-nums">{c.value}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Page() {
  const qc = useQueryClient();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(7);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);


  const listingsQuery = useQuery({ queryKey: ["admin-listings"], queryFn: fetchAllListings });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-listings"] });

  const now = Date.now();
  const statusItems = [
    { key: "pending", label: "🕐 Gözləyən", filter: (l: AdminListing) => l.status === "pending" || l.status === "draft" },
    { key: "active", label: "✅ Təsdiqlənmiş", filter: (l: AdminListing) => l.status === "active" },
    { key: "rejected", label: "❌ Rədd edilmiş", filter: (l: AdminListing) => l.status === "rejected" },
    { key: "promoted", label: "👑 VIP/Premium", filter: (l: AdminListing) => l.badge === "vip" || l.badge === "premium" },
    { key: "expired", label: "⏳ Müddəti bitmiş", filter: (l: AdminListing) => l.badge !== "none" && !!l.badge_expires_at && new Date(l.badge_expires_at).getTime() <= now },
    { key: "deleted", label: "🗑️ Silinmiş", filter: () => false },
    { key: "paymentPending", label: "💳 Ödəniş gözləyən", filter: (l: AdminListing) => l.status === "payment_pending" },
    { key: "all", label: "📃 Bütün elanlar", filter: () => true },
  ].map((s) => ({ ...s, value: listingsQuery.data ? listingsQuery.data.filter(s.filter).length : 0 }));

  const selectedItem = selectedStatus ? statusItems.find((s) => s.key === selectedStatus) : null;
  const statusFiltered = selectedItem && listingsQuery.data ? listingsQuery.data.filter(selectedItem.filter) : [];

  const handleStatusSelect = (key: string) => {
    setSelectedStatus((prev) => {
      if (key === "all") return prev === "all" ? null : "all";
      return key;
    });
  };

  const flagButtons = (a: AdminListing) => (
    <>
      {(["vip", "premium", "store"] as ListingFlag[]).map((f) => {
        const on = hasListingFlag(a.specs, f);
        return (
          <Button
            key={f}
            size="sm"
            variant={on ? "default" : "outline"}
            className="rounded-full text-xs"
            disabled={flagMutation.isPending}
            onClick={() => flagMutation.mutate({ id: a.id, flag: f, on: !on })}
            title="Elanın üzərindəki göstərici"
          >
            {f === "vip" ? "VIP nişanı" : f === "premium" ? "Premium nişanı" : "Mağaza nişanı"}
            {on ? " ✓" : ""}
          </Button>
        );
      })}
    </>
  );

  const statusMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string | undefined }) =>
      setListingStatus(id, status, reason),
    onSuccess: (_d, v) => {
      toast.success(v.status === "active" ? "Elan təsdiqləndi" : v.status === "draft" ? "Elan deaktiv edildi" : "Elan imtina edildi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const planMutation = useMutation({
    mutationFn: ({ id, badge, days }: { id: string; badge: "none" | "vip" | "premium"; days?: number }) =>
      setListingBadge(id, badge, days ?? 7),
    onSuccess: (_d, v) => {
      toast.success(v.badge === "none" ? "Paket ləğv edildi" : "Elan paketi yeniləndi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });


  const flagMutation = useMutation({
    mutationFn: ({ id, flag, on }: { id: string; flag: ListingFlag; on: boolean }) =>
      setListingFlag(id, flag, on),
    onSuccess: () => {
      toast.success("Elanın göstəriciləri yeniləndi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteListing(id),
    onSuccess: () => {
      toast.success("Elan silindi");
      refresh();
    },
    onError: (e) => toast.error(azError(e)),
  });

  const listings = listingsQuery.data ?? [];
  const filtered = listings.filter((l) =>
    `${l.title} ${l.city} ${l.category}`.toLowerCase().includes(search.toLowerCase()),
  );

  const card = (a: AdminListing) => (
    <div
      key={a.id}
      className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-3 shadow-card"
    >
      <img
        src={a.images?.[0] ?? PLACEHOLDER_IMAGE}
        alt=""
        loading="lazy"
        className="size-16 rounded-xl object-cover"
      />
      <div className="min-w-0 flex-1">
        <Link to="/elan/$id" params={{ id: adSlug({ id: a.id, title: a.title, city: a.city }) }} className="truncate font-semibold hover:text-primary">
          {a.title}
        </Link>
        <p className="text-xs text-muted-foreground">
          {a.city} • {formatPrice(Number(a.price))} • {statusLabels[a.status] ?? a.status}
        </p>
      </div>
      <Input
        value={reasons[a.id] ?? ""}
        onChange={(e) => setReasons((r) => ({ ...r, [a.id]: e.target.value }))}
        placeholder="İmtina səbəbi (istəyə bağlı)"
        className="h-9 w-full rounded-full sm:w-56"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          className="rounded-full"
          disabled={statusMutation.isPending}
          onClick={() => statusMutation.mutate({ id: a.id, status: "active" })}
        >
          <Check className="size-4" /> Təsdiqlə
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          disabled={statusMutation.isPending}
          onClick={() => statusMutation.mutate({ id: a.id, status: "rejected", reason: reasons[a.id] })}
        >
          <X className="size-4" /> İmtina et
        </Button>
        <Link to="/elan-redakte/$id" params={{ id: a.id }}>
          <Button size="sm" variant="outline" className="rounded-full">
            <Pencil className="size-4" /> Redaktə
          </Button>
        </Link>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full text-xs"
          onClick={() => planMutation.mutate({ id: a.id, badge: "vip", days })}
        >
          VIP {days}g
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full text-xs"
          onClick={() => planMutation.mutate({ id: a.id, badge: "premium", days })}
        >
          Premium {days}g
        </Button>
        {flagButtons(a)}
        <Button
          size="sm"
          variant="ghost"
          className="rounded-full text-destructive"
          onClick={() => {
            if (confirm("Elan həmişəlik silinsin?")) deleteMutation.mutate(a.id);
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

    </div>
  );

  return (
    <div>
      <AdminPageHeader
        title="Elanlar və moderasiya"
        description="Elanları təsdiqlə, imtina et, VIP/Premium et və ya sil"
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card p-3 text-sm shadow-card">
        <span className="font-semibold">Paket müddəti (balansdan asılı olmayaraq):</span>
        {[1, 7, 30].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            className="rounded-full text-xs"
            onClick={() => setDays(d)}
          >
            {d} gün
          </Button>
        ))}
      </div>


      <StatusAccordion items={statusItems} onSelect={handleStatusSelect} selected={selectedStatus} />

      {selectedStatus === "promoted" ? (
        <div className="mt-6">
          <AdminCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlıq</TableHead>
                  <TableHead>Paket</TableHead>
                  <TableHead>Şəhər</TableHead>
                  <TableHead>Qiymət</TableHead>
                  <TableHead className="text-right">Əməliyyat</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statusFiltered.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="max-w-60 truncate font-medium">
                      <Link
                        to="/elan/$id"
                        params={{ id: adSlug({ id: a.id, title: a.title, city: a.city }) }}
                        className="hover:text-primary"
                      >
                        {a.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge className="rounded-full" variant={a.badge === "vip" ? "default" : "secondary"}>
                        <Crown className="mr-1 size-3" />
                        {a.badge === "vip" ? "VIP" : "Premium"}
                      </Badge>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {a.badge_expires_at
                          ? `Bitir: ${new Date(a.badge_expires_at).toLocaleDateString("az-AZ", { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "numeric" })}`
                          : "Müddətsiz"}
                      </div>
                    </TableCell>
                    <TableCell>{a.city}</TableCell>
                    <TableCell>{formatPrice(Number(a.price))}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs"
                          onClick={() => planMutation.mutate({ id: a.id, badge: "vip", days })}
                        >
                          VIP {days}g
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs"
                          onClick={() => planMutation.mutate({ id: a.id, badge: "premium", days })}
                        >
                          Premium {days}g
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full text-xs"
                          onClick={() => planMutation.mutate({ id: a.id, badge: "none" })}
                        >
                          Paketi ləğv et
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {statusFiltered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      VIP və ya Premium elan yoxdur.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AdminCard>
        </div>
      ) : selectedStatus ? (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {selectedItem?.label}
              <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold tabular-nums">
                {statusFiltered.length}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs"
              onClick={() => setSelectedStatus(null)}
            >
              Geri
            </Button>
          </div>
          {statusFiltered.map(card)}
          {statusFiltered.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-card">
              Bu statusda elan yoxdur.
            </div>
          )}
        </div>
      ) : selectedStatus === "all" ? (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {selectedItem?.label}
              <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold tabular-nums">
                {statusFiltered.length}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs"
              onClick={() => setSelectedStatus(null)}
            >
              Geri
            </Button>
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Başlıq, şəhər və ya kateqoriya üzrə axtar"
            className="h-11 rounded-full"
          />
          <div className="space-y-2">
            {filtered.map(card)}
            {filtered.length === 0 && !listingsQuery.isLoading && (
              <div className="rounded-2xl border border-border bg-card p-4 text-center text-sm text-muted-foreground shadow-card">
                Elan tapılmadı.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
